import { motion } from "motion/react";
import { BookOpen, AlertCircle, Sparkles, Sun, Database, FileText, ArrowRight } from "lucide-react";

export default function PlantLearningCenter() {
  const cards = [
    {
      title: "Plant Care Guides",
      description: "Learn essential parameters about watering intervals, direct/indirect sunlight, suitable potting soil mixes, and daily cultivation tips.",
      icon: <BookOpen className="w-5 h-5 text-emerald-600" />,
      tag: "CULTIVATION",
      actionLabel: "Read Care Guides",
      action: "blog"
    },
    {
      title: "Common Plant Diseases",
      description: "Identify foliage issues early by exploring symptoms of leaf yellowing, powdery mildew, root rot, and pest damage on indoor and outdoor species.",
      icon: <AlertCircle className="w-5 h-5 text-rose-600" />,
      tag: "DIAGNOSTICS",
      actionLabel: "Analyze Disease Indicators",
      action: "blog"
    },
    {
      title: "Indoor Gardening Tips",
      description: "Understand specific room placement guidelines, decorative alignment, passive humidifier recommendations, and matching pot sizes for indoor greenery.",
      icon: <Sparkles className="w-5 h-5 text-amber-600" />,
      tag: "INTERIORS",
      actionLabel: "Explore Indoor Tips",
      action: "blog"
    },
    {
      title: "Seasonal Plant Care",
      description: "Adjust schedules for changing weather—learn about autumn pruning, winter dormancy routines, spring propagation, and intense summer hydration strategies.",
      icon: <Sun className="w-5 h-5 text-blue-600" />,
      tag: "SEASONAL",
      actionLabel: "View Seasonal Care",
      action: "blog"
    },
    {
      title: "Plant Database",
      description: "Search and filter through our comprehensive botanical records containing verified taxonomic descriptions and foliage care metrics.",
      icon: <Database className="w-5 h-5 text-indigo-600" />,
      tag: "ARCHIVES",
      actionLabel: "Explore Plant Database",
      action: "/database"
    },
    {
      title: "Botanical Articles",
      description: "Read peer-reviewed essays on plant science, structural cellular photosynthesizing, ecosystem preservation, and taxonomy history.",
      icon: <FileText className="w-5 h-5 text-teal-600" />,
      tag: "SCIENCE",
      actionLabel: "Read Botanical Articles",
      action: "blog"
    }
  ];

  const handleNavigateOrScroll = (action: string) => {
    if (action.startsWith("/")) {
      window.history.pushState(null, "", action);
      window.dispatchEvent(new Event("popstate"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const el = document.getElementById(action);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <section id="learning-center" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16 space-y-3">
          <span className="text-[10px] text-green-650 font-extrabold uppercase tracking-widest block">LEARNING MATERIAL</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-none">
            Plant Learning Center
          </h2>
          <p className="text-sm sm:text-base text-gray-550 font-medium">
            Acquire verified gardening skills, taxonomic insights, and seasonal care steps through structured educational literature.
          </p>
        </div>

        {/* Content Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              whileHover={{ y: -4, border: "1px solid rgba(22,163,74,0.18)" }}
              className="bg-gray-50 border border-gray-150 rounded-2xl p-6 flex flex-col justify-between hover:bg-white hover:shadow-xs transition-all duration-200"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] bg-green-55/60 border border-green-200 text-green-700 px-2.5 py-0.5 rounded-md font-extrabold tracking-wider">
                    {card.tag}
                  </span>
                  <div className="w-9 h-9 bg-white border border-gray-100 rounded-lg flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-gray-950 tracking-tight">
                    {card.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed font-semibold">
                    {card.description}
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={() => handleNavigateOrScroll(card.action)}
                  className="inline-flex items-center gap-1 text-xs font-black text-green-700 hover:text-green-800 focus:underline cursor-pointer"
                >
                  <span>{card.actionLabel}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
